// src/pages/WorkerConfigs.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { SlidersHorizontal, Plus, Edit2, Trash2, X, RefreshCw } from 'lucide-react';
import { workerConfigsService, WorkerConfig } from 'api/services/workerConfigs.service';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

// Known workers + the keys each actually reads (for the editor hint + templates).
// Values shown are the runtime defaults; an override row only needs the keys you
// want to change.
const TEMPLATES: Record<string, Record<string, any>> = {
  'cdr-worker': {
    concurrency: 1,
    maxRetries: 5,
    readCount: 16,
    readBlockMs: 5000,
    dedupTtlSeconds: 21600,
    claimMinIdleMs: 60000,
    retryBackoffBaseMs: 250,
    keepParkEvents: false,
  },
  'webhook-consumer': {
    batchSize: 20,
    maxRetries: 3,
    pollInterval: 500,
  },
};

const empty = (): WorkerConfig => ({ worker_name: '', config: {}, enabled: true, notes: '' });

const pretty = (c?: Record<string, any> | null) => JSON.stringify(c || {}, null, 2);

const KeyChips: React.FC<{ config?: Record<string, any> | null }> = ({ config }) => {
  const entries = Object.entries(config || {});
  if (!entries.length) return <span className="text-xs text-gray-400">(env defaults)</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 6).map(([k, v]) => (
        <span key={k} className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-mono">
          {k}={String(v)}
        </span>
      ))}
      {entries.length > 6 && <span className="text-xs text-gray-400">+{entries.length - 6}</span>}
    </div>
  );
};

const WorkerConfigs: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canManage = ['master_admin', 'super_admin', 'service_admin', 'provider_owner', 'provider_admin'].includes(role || '');

  const [rows, setRows] = useState<WorkerConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await workerConfigsService.list();
    if (res.success) setRows(res.data || []); else setError(res.error || 'Failed to load');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<WorkerConfig | null>(null);
  const [form, setForm] = useState<WorkerConfig>(empty());
  const [configText, setConfigText] = useState('{}');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm(empty()); setConfigText('{}'); setFormErr(null); setModal(true); };
  const openEdit = (w: WorkerConfig) => { setEditing(w); setForm({ ...empty(), ...w }); setConfigText(pretty(w.config)); setFormErr(null); setModal(true); };

  const applyTemplate = (name: string) => {
    const t = TEMPLATES[name];
    if (t) setConfigText(JSON.stringify(t, null, 2));
  };

  const save = async () => {
    setFormErr(null);
    if (!form.worker_name.trim()) { setFormErr('worker_name is required'); return; }
    let config: Record<string, any>;
    try {
      config = configText.trim() ? JSON.parse(configText) : {};
    } catch {
      setFormErr('config is not valid JSON'); return;
    }
    if (typeof config !== 'object' || Array.isArray(config)) { setFormErr('config must be a JSON object'); return; }
    setSaving(true);
    // Send only the editable fields. NOT id/created_at/updated_at — the backend
    // upserts by worker_name and owns the timestamps, and echoing its epoch-millis
    // timestamps back fails LocalDateTime deserialization (400).
    const res = await workerConfigsService.upsert({
      worker_name: form.worker_name.trim(),
      config,
      enabled: form.enabled,
      notes: form.notes,
    });
    setSaving(false);
    if (res.success) { setModal(false); load(); } else setFormErr(res.error || 'Save failed');
  };

  const remove = async (w: WorkerConfig) => {
    if (!w.id || !window.confirm(`Delete config for "${w.worker_name}"? The worker reverts to its env defaults.`)) return;
    const res = await workerConfigsService.remove(w.id);
    if (res.success) load(); else alert(res.error || 'Delete failed');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><SlidersHorizontal className="w-6 h-6 text-blue-600" /> Worker Config</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Runtime tuning for background workers (cdr-worker, webhook-consumer). cpass is the source of truth and pushes to the runtime — changes apply within ~10s, no redeploy. An empty/disabled row = pure env defaults.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          {canManage && <button onClick={openCreate} className="tl-btn tl-btn-primary"><Plus className="w-5 h-5" /> Add Worker</button>}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      {loading ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>{['Worker', 'Status', 'Config', 'Notes', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">No worker overrides — every worker is running on its env defaults. Add one to tune.</td></tr>}
                {rows.map(w => (
                  <tr key={w.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white font-mono">{w.worker_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${w.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {w.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3"><KeyChips config={w.config} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{w.notes || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(w)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit2 className="w-4 h-4" /></button>
                        {canManage && <button onClick={() => remove(w)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{editing ? `Edit ${editing.worker_name}` : 'Add Worker Config'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {formErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{formErr}</div>}
              <div className="grid grid-cols-2 gap-4">
                <F label="Worker name">
                  <input
                    list="worker-name-suggestions"
                    value={form.worker_name}
                    onChange={e => setForm({ ...form, worker_name: e.target.value })}
                    disabled={!!editing}
                    className={`${INPUT} font-mono disabled:opacity-60`}
                    placeholder="cdr-worker"
                  />
                  <datalist id="worker-name-suggestions">
                    {Object.keys(TEMPLATES).map(n => <option key={n} value={n} />)}
                  </datalist>
                </F>
                <F label="Status">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300 mt-2">
                    <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                    Enabled <span className="text-xs text-gray-400">(off = ignore this row, use env defaults)</span>
                  </label>
                </F>
              </div>
              <F label="Notes"><input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className={INPUT} placeholder="why these values / who tuned" /></F>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">config (JSON — overrides over env defaults)</label>
                  {TEMPLATES[form.worker_name.trim()] && (
                    <button type="button" onClick={() => applyTemplate(form.worker_name.trim())} className="text-xs text-blue-600 hover:underline">
                      Insert {form.worker_name.trim()} template
                    </button>
                  )}
                </div>
                <textarea value={configText} onChange={e => setConfigText(e.target.value)} rows={12} className={`${INPUT} font-mono text-sm`} spellCheck={false} placeholder='{ "concurrency": 4, "maxRetries": 7 }' />
                {TEMPLATES[form.worker_name.trim()] && (
                  <p className="text-xs text-gray-400 mt-1">Known keys: {Object.keys(TEMPLATES[form.worker_name.trim()]).join(', ')}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">{saving && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}{editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>{children}</div>
);

export default WorkerConfigs;
